import apiBaseUrl from '../../utils/config.js';

class ImageService {
  constructor() {
    this.baseUrl = `${apiBaseUrl}/images`;
  }

  getToken() {
    return localStorage.getItem('jwt');
  }

  getAuthHeaders() {
    const token = this.getToken();

    return {
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async parseTextResponse(response, fallbackMessage) {
    const text = await response.text();

    if (!response.ok) {
      try {
        const json = JSON.parse(text);
        throw new Error(json.error || json.message || fallbackMessage);
      } catch {
        throw new Error(text || fallbackMessage);
      }
    }

    return text;
  }

  async uploadImage(file, filename, directory) {
    if (!(file instanceof File)) {
      throw new Error('No valid image file selected.');
    }

    if (!filename || !filename.trim()) {
      throw new Error('Image filename is required.');
    }

    if (!directory || !directory.trim()) {
      throw new Error('Image directory is required.');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', filename);
    formData.append('directory', directory);

    const response = await fetch(`${this.baseUrl}/upload`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: formData,
    });

    return await this.parseTextResponse(response, 'Image upload failed');
  }

  cleanDirectory(directory) {
    if (!directory || typeof directory !== 'string') {
      return '';
    }

    return directory
      .replace(/^\/+/, '')
      .replace(/\/+$/, '')
      .replace(/^api\/images\/+/, '')
      .replace(/^images\/+/, '');
  }

  cleanFilename(filename) {
    if (!filename || typeof filename !== 'string' || filename.trim() === '') {
      return '';
    }

    return filename.split('/').pop().trim();
  }

  getSizedFilename(filename, sizeName) {
    const cleanFilename = this.cleanFilename(filename);

    if (!cleanFilename) {
      return '';
    }

    const dotIndex = cleanFilename.lastIndexOf('.');

    if (dotIndex === -1) {
      return `${cleanFilename}_${sizeName}`;
    }

    const name = cleanFilename.slice(0, dotIndex);
    const extension = cleanFilename.slice(dotIndex);

    return `${name}_${sizeName}${extension}`;
  }

  getImageUrl(directory, filename) {
    const cleanFilename = this.cleanFilename(filename);

    if (!cleanFilename) {
      return '/images/default.png';
    }

    const cleanDirectory = this.cleanDirectory(directory);

    if (!cleanDirectory) {
      return `${this.baseUrl}/${encodeURIComponent(cleanFilename)}`;
    }

    return `${this.baseUrl}/${encodeURIComponent(cleanDirectory)}/${encodeURIComponent(cleanFilename)}`;
  }

  getFullImageUrl(directory, filename) {
    return this.getImageUrl(directory, filename);
  }

  getThumbImageUrl(directory, filename) {
    const thumbFilename = this.getSizedFilename(filename, 'thumb');
    return this.getImageUrl(directory, thumbFilename);
  }

  getMediumImageUrl(directory, filename) {
    const mediumFilename = this.getSizedFilename(filename, 'medium');
    return this.getImageUrl(directory, mediumFilename);
  }

  isSlowConnection() {
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (!connection) {
      return false;
    }

    return (
      connection.saveData === true ||
      ['slow-2g', '2g', '3g'].includes(connection.effectiveType)
    );
  }

  getBestImageUrl(directory, filename, type = 'medium') {
    if (!filename || filename.trim() === '') {
      return '/images/default.png';
    }

    const slowConnection = this.isSlowConnection();

    if (slowConnection) {
      if (type === 'lightbox') {
        return this.getMediumImageUrl(directory, filename);
      }

      return this.getThumbImageUrl(directory, filename);
    }

    if (type === 'thumb') {
      return this.getThumbImageUrl(directory, filename);
    }

    if (type === 'medium') {
      return this.getMediumImageUrl(directory, filename);
    }

    if (type === 'lightbox') {
      return this.getFullImageUrl(directory, filename);
    }

    return this.getFullImageUrl(directory, filename);
  }

  async getImage(directory, filename) {
    const url = this.getImageUrl(directory, filename);

    const response = await fetch(url, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch image');
    }

    return await response.blob();
  }

  async deleteImage(directory, filename) {
    const cleanDirectory = this.cleanDirectory(directory);
    const cleanFilename = this.cleanFilename(filename);

    if (!cleanFilename) {
      throw new Error('No image filename provided');
    }

    const url = `${this.baseUrl}/newdir/${encodeURIComponent(cleanDirectory)}/${encodeURIComponent(cleanFilename)}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    return await this.parseTextResponse(response, 'Failed to delete image');
  }
}

export { ImageService };

export default new ImageService();